<template>
  <div v-if="!documentUploadStore">Loading store...</div>
  <div v-else>
  <div>
    <Stack class="stk-py-8">
      <div class="columns">
        <div class="column is-three-quarters" >
          <Title size="8">
            Documents
          </Title>
          <Subtitle size="6">
            All the documents used to fill out the Dossier information 
          </Subtitle>
        </div>
        <div class="column is-one-quarter">
          <!-- Auto-Fill Forms Button -->
          <div class="columns is-vcentered mb-0 pb-0">
            <div class="column">
              <p class="tw-font-bold tw-mb-2 tw-text-sm">
                Need to fill out forms?
              </p>
            </div>
            <div class="column has-text-right">
              <a 
                :href="`/ui/advisor/form-upload?clientId=${entityExternalId}`" 
                target="_blank" 
                class="stk-button button stk-size-3 "
              >
                <span class="icon has-text-primary"><i class="fas fa-rocket"></i></span>
                <span>Auto-Fill Forms</span>
              </a>
            </div>
          </div>
          <p class="tw-text-xs">Use our auto-fill feature to automatically complete forms with your document data</p>
        </div>
      </div>

      <div class="tw-flex tw-items-start tw-gap-12 tw-flex-wrap tw-mt-4">
        <div class="tw-flex-1">
          <DocMasterUpload
            :entity-external-id="entityExternalId"
            :csrf-token="csrfToken"
            @documents-updated="refreshDocuments"
          />
        </div>
        <!-- TODO: Uncomment below when ready to make the Request Documents button funcional -->
        <!-- <Stack v-if="hasClientPortalFeature" class="tw-flex tw-flex-col tw-gap-4 tw-max-w-xs tw-items-start">
          <div class="tw-px-4 tw-text-gray-500 tw-text-lg">— OR —</div>
        <Stack gap="2">
          <Title size="5">
            Have Client Upload Documents
          </Title>
          <Button
            variant="primary"
            @click="$emit('open-portal-actions')"
          >
            <span>Request Documents from Client</span>
          </Button>

          <Callout v-if="entity && entity.portal_status === 'not_invited'" status="danger">
            Client needs portal access first
          </Callout>
        </Stack>
      </Stack> -->
      </div>

      <!-- Document Checklist Section - NEW -->
      <StakeModal v-model="showDocumentChecklistModal">
        <Stack v-if="showDocumentChecklistModal">
          <DocumentChecklist :entityExternalId="entityExternalId" />
        </Stack>
      </StakeModal>

      <!-- Past Documents Section -->
      <template v-if="pastDocumentsReady">
        <Stack v-if="pastDocuments.length">
          <Title>Past Uploaded Documents</Title>
        
          <div class="field is-grouped">
            <div class="control tw-grow">
              <input
                v-model="filterText"
                class="stk-input tw-w-full"
                type="text"
                placeholder="Filter by file name or classification..."
              />
            </div>
            <!-- TODO: UNCOMMENT BELOW WHEN WE WANT TO ADD VALIDATION STATUS FILTER BACK -->
            <div class="control">
              <div class="stk-select">
                <select v-model="validationStatusFilter">
                  <option value="">All Validation Status</option>
                  <option value="passed">All Checks Passed</option>
                  <option value="issues">Review DocCheckup</option>
                </select>
                <Icon />
              </div>
            </div>
          </div>

          <!-- Table -->
          <DocumentTable
            :isPast="true"
            :documents="sortedAndFilteredDocuments"
            :entityExternalId="entityExternalId"
            :headers="headers"
            mode="advisor"
            @document-done="refreshDocuments"
            @document-deleted="refreshDocuments"
            @delete-requested="confirmDelete"
          />
        </Stack>
      </template>

      <LoaderWithText v-else>
        Fetching Uploaded Documents…
      </LoaderWithText>
    </Stack>

    <!-- Learn More Modal -->
    <div class="modal" :class="{ 'is-active': showModal }">
      <div class="modal-background" @click="showModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Supported Document Types</p>
          <button class="delete" @click="showModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p><strong>We extract information from any document! We currently optimize extraction for these document types:</strong></p>
          <ul>
            <li>📝 W-2, W-4, 1040, 1098, 1099 tax documents</li>
            <li>🆔 ID documents (Driver's license, passport, state ID)</li>
            <li>📜 Brokerage statements </li>
          </ul>
          <p class="mt-3">
            We are continuously improving our extraction for more document types, check back soon!
          </p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-link" @click="showModal = false">Got it!</button>
        </footer>
      </div>
    </div>

    <!-- Confirmation Modal for Delete  -->
    <div class="modal" :class="{ 'is-active': showDeleteModal }">
      <div class="modal-background" @click="closeDeleteModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Delete</p>
          <button class="delete" aria-label="close" @click="closeDeleteModal"></button>
        </header>
        <section class="modal-card-body">
          <p>Are you sure you want to delete the document <strong>{{ deleteFilename }}</strong>?</p>
          <p class="has-text-danger">This action cannot be undone.</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" @click="deleteDocument">Delete</button>
          <button class="button" @click="closeDeleteModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</div>
</template>

<script>
// import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
import {
  Badge,
  Button,
  Buttons,
  Callout,
  Dropzone,
  FileInput,
  Icon,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Stack,
  Subtitle,
  Title,
  StakeModal,
} from '@stakeui';
import { cleanFileName } from '../../../../shared/Util';
import DocMasterUpload from '../components/DocMasterUpload.vue';
import DocumentTable from '../components/DocumentTable.vue';
import DocumentChecklist from '../components/DocumentChecklist2.vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useDocumentUploadStore } from '@frontend/ui/stores/DocmasterStore';


export default {
    name: 'DocMasterAdvisor',
    props: {
        entityExternalId: {
            type: String,
            required: true
        },
    },
    data() {
        return {
            showModal: false,
            fileNames: [],
            showExtractInfoModal: false,
            showValidationDetails: false,
            selectedDocExtractInfo: [],
            selectedDocument: null,
            filterText: "", // Filter input
            sortKey: "uploaded_at", // Current column to sort by
            sortAscending: true, // Sort direction
            validationStatusFilter: "",
            showDeleteModal: false, // Show/hide delete modal
            deleteDocumentId: null, // ID of the document to delete
            deleteFilename: "", // Name of the document to display in the modal
            showDocumentChecklistModal: false,
            documentUploadStore: null,
            pastDocumentsReady: false, 
            updateTrigger: 0,
        };
    },

    computed: {
        headers() {
            return {
                'X-CSRFToken': this.$root.$data.csrfToken,
            };
        },
        csrfToken() {
          return this.$root.$data.csrfToken;
        },
        entityStateInitialized() {
          return (
            this.documentUploadStore &&
            this.documentUploadStore.entityDocuments &&
            this.documentUploadStore.entityDocuments[this.entityExternalId]

          );
        },
        advisorStore: () => useAdvisorStore(),

        pastDocuments() {
          this.updateTrigger;
          const store = this.documentUploadStore;
          if (!store.entityDocuments || !store.entityDocuments[this.entityExternalId]) return [];
          return store.getPastDocumentsForEntity(this.entityExternalId);
        },
        isUploadingDocuments() {
          return this.documentUploadStore.uploadStates[this.entityExternalId]?.isUploading || false;
        },
        hasFetchedDocuments() {
          const fetchedMap = this.documentUploadStore.entityPastDocumentsFetched;
          return !!(fetchedMap && fetchedMap[this.entityExternalId]);
        },
        recentUploads() {
          return this.documentUploadStore.getRecentUploadsForEntity(this.entityExternalId);
        },

        hasClientPortalFeature() {
          return this.advisorStore.featureFlags.includes('ClientPortal');
        },
        reversedDocuments() {
            return [...this.pastDocuments].reverse();
        },
        sortedAndFilteredDocuments() {
          let documents = this.pastDocuments.filter(doc => {
            const search = this.filterText.toLowerCase();
            return (
              doc.filename.toLowerCase().includes(search) ||
              (doc.classification || "").toLowerCase().includes(search)
            );
          });

          if (this.validationStatusFilter) {
            documents = documents.filter(doc => {
              const allChecksPassed = Array.isArray(doc.validation) && doc.validation.every(v => v.passed);
              return this.validationStatusFilter === "passed" ? allChecksPassed : !allChecksPassed;
            });
          }

          if (this.sortKey) {
            documents.sort((a, b) => {
              let result = 0;
              if (a[this.sortKey] < b[this.sortKey]) result = -1;
              if (a[this.sortKey] > b[this.sortKey]) result = 1;
              return this.sortAscending ? result : -result;
            });
          }

          return documents.reverse(); // newest first
        },

    },
    created() {
      this.cleanFileName = cleanFileName;
      this.documentUploadStore = useDocumentUploadStore();
      this.documentUploadStore.setMode('advisor'); // enable full processing

      this.documentUploadStore
        .setCurrentEntityWithHistory(this.entityExternalId)
        .then(() => {
          // Confirm that the store is populated
          const fetchedMap = this.documentUploadStore.entityPastDocumentsFetched;

          if (fetchedMap?.[this.entityExternalId]) {
            this.pastDocumentsReady = true;
          } else {
            console.warn('Store fetch did not populate expected key:', this.entityExternalId);
          }
        })
        .catch(err => {
          console.error('Failed to set entity with history:', err);
        });
    },




    methods: {
      async refreshDocuments() {
        try {
          await this.documentUploadStore.refreshPastDocuments(this.entityExternalId);
        } catch (e) {
          console.error("Error refreshing past documents:", e);
        }
      },
        confirmDelete(documentId, filename) {
        // Show confirmation modal and set document info
        this.deleteDocumentId = documentId;
        this.deleteFilename = filename;
        this.showDeleteModal = true;
        },
        closeDeleteModal() {
            // Close the delete modal
            this.showDeleteModal = false;
            this.deleteDocumentId = null;
            this.deleteFilename = "";
        },
        async deleteDocument() {
          try {
            await this.documentUploadStore.deleteDocument(
              this.entityExternalId,
              this.deleteDocumentId,
              this.csrfToken
            );

            this.closeDeleteModal();

            // re-fetch to reflect changes immediately
            await this.refreshDocuments();
            this.updateTrigger++;
          } catch (error) {
            console.error("Error deleting document:", error);
            this.closeDeleteModal();
          }
        },
        showDocumentDetails(doc) {
            this.selectedDocument = {
                ...doc,
                extract_info: doc.extract_info || [],
                file_name: doc.filename || '',
                classification: doc.classification || 'Unclassified',
                download_url: doc.download_url || doc.url,
                external_id: doc.external_id || doc.documentId
            };
            document.body.style.overflow = 'hidden';
        },

        closeDocumentDetails() {
            this.selectedDocument = null;
            // Restore body scrolling
            document.body.style.overflow = 'auto';
        },

        saveExtractInfo() {
            console.log("Updated extract info:", this.selectedDocExtractInfo);
            this.selectedDocument = null;
            this.selectedDocExtractInfo = [];
        },
    },
    components: {
      DocMasterUpload,
      DocumentTable,
      DocumentChecklist,
      Button,
      Buttons,
      Callout,
      Dropzone,
      FileInput,
      Icon,
      Level,
      LevelLeft,
      LevelRight,
      LoaderWithText,
      Stack,
      Subtitle,
      Title,
      StakeModal,
      Badge,
    },
    mounted() {
      setTimeout(() => {
        console.log('pastDocuments:', this.pastDocuments);
        console.log('currentDocuments:', this.documentUploadStore.currentDocuments);
      }, 2000);
      this.$forceUpdate(); // force Vue to re-evaluate render once everything is ready
    },
};
</script>

<style scoped>
.callout {
  align-items: center;
  display: flex;
  border-radius: 1em;
  gap: 1em;
  padding: 1em;
  padding-left: 1.25em;
  background: var(--stk-primary-90);
  color: var(--stk-primary-30);

  p {
    line-height: 1.25;
  }
}

.modal-content {
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 20px;
  overflow-y: auto;
}

.full-screen-content {
  height: calc(100vh - 40px); /* Account for padding */
  margin: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal.is-active {
  z-index: 1000;
}

.modal-background {
  background-color: rgba(10, 10, 10, 0.86);
}

/* Outer container keeps everything together */
.upload-container {
  margin: auto;
  padding: 2rem;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1); /* Slight elevation */
}

/* Info Banner Styling */
.info-banner {
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
}

/* Ensures list stays readable */
.info-list {
  text-align: left;
  max-width: 600px;
  margin: auto;
}

/* Upload Button Section */
.upload-box {
  padding: 2rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  margin-top: 1rem;
}

/* Make sure icons & text inside the upload button are centered */
.file-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
}

/* Larger Upload Icon */
.file-icon {
  font-size: 2rem;
}

.auto-fill-banner {
  background-color: var(--stk-primary-90);
  color: var(--stk-primary-30);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 1em;
  padding: 1em 1.25em;
}
</style>
